import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, ViewChild, signal } from '@angular/core';
import { AuthService, User } from '@auth0/auth0-angular';
import { Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './header.html',
  styleUrl: './header.css'
})
export class Header {
  private readonly theme = signal<'dark' | 'light'>(this.getInitialTheme());
  protected readonly user$: Observable<User | null | undefined>;
  protected readonly menuOpen = signal<boolean>(false);
  protected readonly langMenuOpen = signal<boolean>(false);
  @ViewChild('langButton') private langButtonRef?: ElementRef<HTMLElement>;
  @ViewChild('langMenu') private langMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('avatarButton') private avatarButtonRef?: ElementRef<HTMLElement>;
  @ViewChild('userMenu') private userMenuRef?: ElementRef<HTMLElement>;
  protected readonly languages = [
    { code: 'en', labelKey: 'lang.english' },
    { code: 'es', labelKey: 'lang.spanish' }
  ];
  protected readonly currentLang = signal<string>('en');

  constructor(private auth: AuthService, private translate: TranslateService) {
    this.applyTheme(this.theme());
    this.user$ = this.auth.user$;
    const savedLang = localStorage.getItem('lang');
    const initial = savedLang || this.translate.currentLang || this.translate.defaultLang || 'en';
    if (initial && initial !== this.translate.currentLang) {
      this.translate.use(initial);
    }
    this.currentLang.set(initial);
    this.translate.onLangChange.subscribe(e => {
      this.currentLang.set(e.lang);
      localStorage.setItem('lang', e.lang);
    });
  }

  protected toggleTheme(): void {
    const next = this.theme() === 'dark' ? 'light' : 'dark';
    this.theme.set(next);
    this.applyTheme(next);
  }

  protected isLightTheme(): boolean {
    return this.theme() === 'light';
  }

  protected toggleUserMenu(): void {
    const next = !this.menuOpen();
    this.menuOpen.set(next);
    if (next) {
      this.langMenuOpen.set(false);
    }
  }
  protected isMenuOpen(): boolean { return this.menuOpen(); }

  protected toggleLangMenu(): void {
    const next = !this.langMenuOpen();
    this.langMenuOpen.set(next);
    if (next) {
      this.menuOpen.set(false);
    }
  }
  protected isLangMenuOpen(): boolean { return this.langMenuOpen(); }
  protected currentLanguage(): string { return this.currentLang(); }
  protected selectLanguage(code: string): void {
    if (code === this.currentLang()) return;
    this.translate.use(code);
  }

  protected login(): void { this.auth.loginWithRedirect(); }
  protected logout(): void {
    try {
      localStorage.removeItem('bp:lastSelectedName');
      localStorage.removeItem('carrier:lastSelectedApiKey');
      localStorage.removeItem('carrier:lastSelectedName');
    } catch {}
    this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  private getInitialTheme(): 'dark' | 'light' {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return 'dark';
  }

  private applyTheme(theme: 'dark' | 'light'): void {
    const root = document.documentElement;
    if (theme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else {
      root.removeAttribute('data-theme');
    }
    localStorage.setItem('theme', theme);
  }

  private isInside(ref: ElementRef<HTMLElement> | undefined, target: EventTarget | null): boolean {
    if (!ref) return false;
    const node = target as Node | null;
    if (!node) return false;
    return ref.nativeElement.contains(node);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    const clickInsideLang = this.isInside(this.langButtonRef, target) || this.isInside(this.langMenuRef, target);
    const clickInsideUser = this.isInside(this.avatarButtonRef, target) || this.isInside(this.userMenuRef, target);

    // Outside both: close both menus
    if (!clickInsideLang && !clickInsideUser) {
      if (this.langMenuOpen()) this.langMenuOpen.set(false);
      if (this.menuOpen()) this.menuOpen.set(false);
      return;
    }

    // If interacting with one menu, close the other (mutually exclusive)
    if (clickInsideLang && this.menuOpen()) {
      this.menuOpen.set(false);
    } else if (clickInsideUser && this.langMenuOpen()) {
      this.langMenuOpen.set(false);
    }
  }
}
